import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Prop, Watch } from '@stencil/core';
import { FlexContentOptions, PredefinedColors, TextAlignOptions } from '../../interface';

/**
 * Figma design link
 * @figma https://www.figma.com/design/ngaGX6aSwvCpJxDwjhd38h/Input-and-button?node-id=1304-7379&m=dev
 */

@Component({
    tag: 'rs-radio',
    styleUrl: 'radio.css',
    shadow: true
})
export class Radio implements ComponentInterface {
    private radioGroup: HTMLRsRadioGroupElement | null = null;
    private runWatch: boolean = true;

    /**
     * The host element within the class instance.
     * This returns an instance of HTMLStencilElement which extends HTMLElement, so standard DOM methods/events can be used.
     */
    @Element() el!: HTMLRsRadioElement;

    /**
     * Checks if the radio is checked or  not.
     * Any changes on this property will cause the render function to be called again
     */
    @Prop({ mutable: true }) checked = false;

    /**
     * (optional) 'primary' | 'secondary' | 'secondary-variant' | 'positive' | 'positive-variant' | 'white' | 'light' | 'grey-light' | 'grey' | 'black' | 'heading' | 'negative' | 'neutral' | 'highlight'
     * Colour of the inner circle, the outline will just be a standard grey colour.
     * You can pass custom colour in hex or rgb format (e.g. '#ffffff' or 'rgb(12,32,79)').
     */
    @Prop() color: PredefinedColors | string;

    /**
     * The name of this radio control, which will be submitted with the form data.
     */
    @Prop() name: string;

    /**
     * If disabled, the user will not be able to interact with the radio.
     */
    @Prop() disabled = false;

    /**
     * The value of the radio.
     */
    @Prop() value?: any | null;

    /**
     * Changes the look of the radio component to a grey outline button
     */
    @Prop() button?: 'vertical' | 'horizontal' | 'default' = 'default';

    /**
     * Possible values are: flex-start, flex-end, space-around, space-between, space-evenly
     * Defines how we want to align the elements inside the radio button
     */
    @Prop() justifyContent: FlexContentOptions;

    /**
     * Possible values are: left, center, right
     * Defines how we want to align the text inside the radio button
     */
    @Prop() textAlign: TextAlignOptions;

    /**
     * Defines whether a user clicking can change the state of the radio.
     */
    @Prop() editable: boolean = true;

    /**
     * An event emitted when the styles of the radio change.
     */
    @Event() rsStyle: EventEmitter;

    /**
     * Watch for any changes on the `checked` property.
     */
    @Watch('checked')
    protected isCheckedChange() {
        this.emitStyle();
        if (this.radioGroup === null && this.runWatch) {
            this.runWatch = true;
            this.setSelected(this.checked);
        }
    }

    /**
     * Watch for any changes on the `disabled` property.
     */
    @Watch('disabled')
    protected disabledChanged() {
        this.emitStyle(); // disabled changed, emit change in styling
    }

    /**
     * As soon as the component is attached to the DOM,
     * we make sure that a default radio is selected.
     */
    connectedCallback() {
        const radioGroup = this.radioGroup = this.el.closest('rs-radio-group');
        if (radioGroup !== null) {
            this.updateState();
            radioGroup.addEventListener('rsChange', this.updateState);
        }
    }

    disconnectedCallback() {
        const radioGroup = this.radioGroup;
        if (radioGroup !== null) {
            radioGroup.removeEventListener('rsChange', this.updateState);
            this.radioGroup = null;
        }
    }

    componentWillLoad() {
        this.emitStyle();
    }

    /**
     * Updates checked state
     */
    private updateState = () => {
        if (this.radioGroup === null) {
            return;
        }

        this.checked = this.radioGroup.value === this.value;
    }

    /**
     * Emits `rsStyle` event when the styles of the radio change
     */
    private emitStyle() {
        this.rsStyle.emit({
            'radio': true,
            'radio-checked': this.checked,
            'interactive-disabled': this.disabled
        });
    }

    private handleClick = () => {
        if (this.editable === false) {
            return;
        }

        this.setSelected();
    }

    private setSelected = (state = !this.checked) => {
        if (this.disabled) {
            return;
        }

        if (this.radioGroup !== null) {
            const currentValue = this.radioGroup.value;
            const newValue = this.value;
            if (newValue !== currentValue) {
                this.radioGroup.value = newValue;
            } else if (this.radioGroup.allowDeselect) {
                this.radioGroup.value = undefined;
            }
        } else {
            this.runWatch = false;
            this.checked = state;
        }
    }

    render() {
        const { disabled, checked, color } = this;
        const predefinedColors = ['primary', 'secondary', 'secondary-variant', 'positive', 'positive-variant', 'white', 'light', 'grey-light', 'grey', 'black', 'heading', 'negative', 'neutral', 'highlight'];
        const colorIsPredefined = predefinedColors.includes(color);

        return (
            <Host
                role="radio"
                class={ {
                    'nativeElement': true,
                    'radio-checked': checked,
                    'radio-disabled': disabled,
                    'radio-button': this.button === 'vertical' || this.button === 'horizontal',
                    'radio-button--vertical': this.button === 'vertical',
                    'radio-button--horizontal': this.button === 'horizontal'
                } }
                style={ {
                    'justify-content': this.justifyContent
                } }
                onClick={ this.handleClick }
                disabled={ disabled }
            >
                <slot name="start"/>
                <div class="rs-radio__circle-container">
                    <div class="rs-radio__circle-inner" style={ {
                        'background-color': colorIsPredefined ? `var(--rs-color-${ color })` : color
                    } }/>
                </div>
                <slot name="end"/>
            </Host>
        );
    }

}